// Currency utilities and geolocation

export interface Currency {
  code: string;
  symbol: string;
  name: string;
}

export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
];

// Country to currency mapping (major countries)
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // Europe
  'AT': 'EUR', 'BE': 'EUR', 'CY': 'EUR', 'EE': 'EUR', 'FI': 'EUR',
  'FR': 'EUR', 'DE': 'EUR', 'GR': 'EUR', 'IE': 'EUR', 'IT': 'EUR',
  'LV': 'EUR', 'LT': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'NL': 'EUR',
  'PT': 'EUR', 'SK': 'EUR', 'SI': 'EUR', 'ES': 'EUR',
  'GB': 'GBP', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN',
  'CZ': 'CZK', 'HU': 'HUF', 'RO': 'RON', 'CH': 'CHF',
  
  // Americas
  'US': 'USD', 'CA': 'CAD', 'MX': 'USD', // Mexico often uses USD for flights
  
  // Asia Pacific
  'AU': 'AUD', 'NZ': 'NZD', 'JP': 'JPY',
  
  // Default fallback
  'DEFAULT': 'EUR',
};

/**
 * Get currency by country code
 */
export function getCurrencyByCountry(countryCode: string): string {
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || COUNTRY_TO_CURRENCY['DEFAULT'];
}

/**
 * Get currency object by code
 */
export function getCurrency(code: string): Currency | undefined {
  return SUPPORTED_CURRENCIES.find(c => c.code === code);
}

/**
 * Detect user's country using geolocation API
 * Falls back to browser's locale if geolocation fails
 */
export async function detectUserCurrency(): Promise<string> {
  try {
    // Try to get country from IP geolocation (free service)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.country_code) {
        const currency = getCurrencyByCountry(data.country_code);
        console.log(`Detected country: ${data.country_code}, currency: ${currency}`);
        return currency;
      }
    }
  } catch (error) {
    console.warn('Failed to detect country from IP:', error);
  }
  
  // Fallback: try to detect from browser locale
  try {
    const locale = navigator.language || (navigator as any).userLanguage;
    const countryCode = locale.split('-')[1] || locale.split('_')[1];
    if (countryCode) {
      const currency = getCurrencyByCountry(countryCode);
      console.log(`Detected currency from locale: ${currency}`);
      return currency;
    }
  } catch (error) {
    console.warn('Failed to detect currency from locale:', error);
  }
  
  // Final fallback
  return 'EUR';
}

/**
 * Format price with currency symbol
 */
export function formatPrice(amount: number, currencyCode: string): string {
  const currency = getCurrency(currencyCode);
  if (!currency) {
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
  
  // Special handling for currencies without decimals
  if (currencyCode === 'JPY') {
    return `${currency.symbol}${Math.round(amount).toLocaleString()}`;
  }
  
  return `${currency.symbol}${amount.toFixed(2)}`;
}

/**
 * Get exchange rate from EUR to target currency
 * Uses a free exchange rate API
 */
let exchangeRatesCache: { rates: Record<string, number>; timestamp: number } | null = null;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

export async function getExchangeRate(fromCurrency: string, toCurrency: string): Promise<number> {
  if (fromCurrency === toCurrency) {
    return 1;
  }
  
  // Check cache
  if (exchangeRatesCache && Date.now() - exchangeRatesCache.timestamp < CACHE_DURATION) {
    const rate = exchangeRatesCache.rates[`${fromCurrency}_${toCurrency}`];
    if (rate) return rate;
  }
  
  try {
    // Use exchangerate-api.com (free tier)
    const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${fromCurrency}`);
    if (response.ok) {
      const data = await response.json();
      const rate = data.rates[toCurrency];
      
      if (rate) {
        // Cache the rates
        exchangeRatesCache = {
          rates: { [`${fromCurrency}_${toCurrency}`]: rate },
          timestamp: Date.now(),
        };
        return rate;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch exchange rate:', error);
  }
  
  // Fallback: return 1 if conversion fails (no conversion)
  console.warn(`Could not get exchange rate from ${fromCurrency} to ${toCurrency}, using 1.0`);
  return 1;
}

/**
 * Convert price from one currency to another
 */
export async function convertPrice(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  const rate = await getExchangeRate(fromCurrency, toCurrency);
  return amount * rate;
}

